import { useEffect, useState } from "react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { Mail, Send, Eye } from "lucide-react";

/**
 * Admin card that previews and manually fires the weekly Statement Digest
 * email (auto-runs Mon 8am UTC via cron). Preview shows the exact HTML that
 * would be sent so admins can eyeball the numbers before triggering a send.
 */
const StatementDigestCard = ({ lang = "en" }) => {
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/digest/statement/preview");
      setDigest(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load digest");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const sendNow = async () => {
    if (!confirm(lang === "es"
      ? "¿Enviar el digest a todos los administradores ahora?"
      : "Send the digest email to every admin now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/digest/statement/send-now");
      toast.success(
        lang === "es"
          ? `Enviado a ${data.admin_count} administrador${data.admin_count === 1 ? "" : "es"} · ${data.totals.count} extractos`
          : `Sent to ${data.admin_count} admin${data.admin_count === 1 ? "" : "s"} · ${data.totals.count} statements`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  const t = digest?.totals || { count: 0, pass: 0, review: 0, variance: 0, review_pending: 0 };
  const start = (digest?.window_start || "").slice(0, 10);
  const end = (digest?.window_end || "").slice(0, 10);

  return (
    <div className="border border-[#E5E5E5] bg-white px-4 py-4 mt-8" data-testid="statement-digest-card">
      <div className="flex items-start gap-3">
        <Mail className="w-5 h-5 text-[#C4A252] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="rn3-eyebrow text-[#8B1C22]">
            {lang === "es" ? "Digest semanal de extractos" : "Weekly statement digest"}
          </div>
          <p className="text-xs text-[#404040] mt-1 leading-relaxed">
            {lang === "es"
              ? `Se envía automáticamente cada lunes a las 8:00 UTC a todos los administradores. Ventana actual: ${start} → ${end}.`
              : `Auto-emails every admin Monday at 8:00 UTC. Current window: ${start} → ${end}.`}
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px] mt-3 mb-3">
            <div className="bg-[#FDFBF7] border border-[#E5E5E5] px-2 py-1" data-testid="digest-count">
              <div className="text-[9px] uppercase tracking-widest text-[#737373]">Imported</div>
              <div className="font-medium tabular-nums">{t.count}</div>
            </div>
            <div className="bg-[#DCFCE7] border border-[#166534] px-2 py-1" data-testid="digest-pass">
              <div className="text-[9px] uppercase tracking-widest text-[#166534]">Pass</div>
              <div className="font-medium tabular-nums text-[#166534]">{t.pass}</div>
            </div>
            <div className="bg-[#FEF3C7] border border-[#C4A252] px-2 py-1" data-testid="digest-review">
              <div className="text-[9px] uppercase tracking-widest text-[#78350F]">Review</div>
              <div className="font-medium tabular-nums text-[#78350F]">{t.review}</div>
            </div>
            <div className="bg-[#FEE2E2] border border-[#8B1C22] px-2 py-1" data-testid="digest-variance">
              <div className="text-[9px] uppercase tracking-widest text-[#8B1C22]">Variance</div>
              <div className="font-medium tabular-nums text-[#8B1C22]">{t.variance}</div>
            </div>
            <div className="bg-[#FDFBF7] border border-[#E5E5E5] px-2 py-1" data-testid="digest-pending">
              <div className="text-[9px] uppercase tracking-widest text-[#737373]">Txns pending</div>
              <div className="font-medium tabular-nums">{t.review_pending}</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowPreview((s) => !s)}
              className="border border-[#C4A252] text-[#8B1C22] hover:bg-[#FDFBF7] inline-flex items-center gap-1 text-xs px-3 py-1.5 uppercase tracking-widest transition-colors"
              data-testid="digest-preview-btn"
            >
              <Eye className="w-3 h-3" />
              {showPreview ? (lang === "es" ? "Ocultar" : "Hide") : (lang === "es" ? "Vista previa" : "Preview")}
            </button>
            <button
              onClick={sendNow}
              disabled={busy || t.count === 0}
              className="rn3-btn-primary inline-flex items-center gap-1 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
              title={t.count === 0 ? (lang === "es" ? "No hay actividad esta semana" : "No activity this week") : ""}
              data-testid="digest-send-btn"
            >
              <Send className="w-3 h-3" />
              {busy ? "…" : (lang === "es" ? "Enviar ahora" : "Send now")}
            </button>
          </div>
        </div>
      </div>

      {showPreview && digest?.html && (
        <div className="mt-4 border border-[#E5E5E5] bg-[#FAFAF7]" data-testid="digest-preview-body">
          <div className="px-3 py-1 bg-white border-b border-[#E5E5E5] text-[10px] uppercase tracking-widest text-[#737373]">
            {lang === "es" ? "Vista previa del correo" : "Email preview"}
          </div>
          <iframe
            title="digest-preview"
            srcDoc={digest.html}
            className="w-full h-[420px] bg-white"
          />
        </div>
      )}
    </div>
  );
};

export default StatementDigestCard;
